// src/components/FitAnalysisModal.tsx
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonProgressBar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  alertCircleOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  closeOutline,
  codeSlashOutline,
  constructOutline,
  peopleOutline,
  ribbonOutline,
  sparklesOutline,
  starOutline,
  trendingUpOutline,
  warningOutline
} from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { fitAnalysis } from '../services/api.ts';

interface LineItemEvaluation {
  job_requirement: string;
  resume_evidence: string;
  fit_assessment: string;
}

interface AlternativeResume {
  resume_id: string;
  resume_name?: string;
  score: number;
  note?: string;
}

interface ResumeRecommendation {
  recommended_resume_id: string;
  recommended_resume_name?: string;
  confidence_score: number;
  match_summary: string;
  why_this_resume: string;
  alternative_resumes?: AlternativeResume[];
}

interface TailoringSuggestion {
  skill_name: string;
  skill_category: string;
  priority_weight: number;
  is_core_technical: boolean;
  found_in_resume_id?: string;
  found_in_resume_name?: string;
  relevance_score: number;
  reason: string;
  how_to_add?: string;
}

interface TailoringSuggestions {
  total_suggestions: number;
  improvement_potential?: string;
  suggestions: TailoringSuggestion[];
}

interface SkillCategoryScore {
  category_name: string;
  match_score: number;
  matched_skills: number;
  total_skills: number;
  priority_weight: number;
  assessment: string;
}

interface SkillCategoryBreakdown {
  overall_weighted_score: number;
  category_scores: SkillCategoryScore[];
}

interface AnalysisMetadata {
  used_master_resume: boolean;
  total_resumes_analyzed: number;
  analysis_type: string;
}

interface FitAnalysisData {
  role_level_fit: {
    conclusion: string;
    overall_fit_score?: number;
  };
  line_item_evaluation: LineItemEvaluation[];
  skill_category_breakdown?: SkillCategoryBreakdown;
  resume_recommendation?: ResumeRecommendation;
  tailoring_suggestions?: TailoringSuggestions;
  analysis_metadata?: AnalysisMetadata;
}

interface FitAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string | null;
  jobTitle?: string;
  resumeId?: string;
  userSearchId?: string;
  isScrapedJob?: boolean;
}

const FitAnalysisModal: React.FC<FitAnalysisModalProps> = ({ isOpen, onClose, jobId, jobTitle, resumeId, userSearchId, isScrapedJob }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRateLimitError, setIsRateLimitError] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<FitAnalysisData | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [insufficientBalance, setInsufficientBalance] = useState(false);

  // Refs for scrolling to sections
  const matchesRef = useRef<HTMLDivElement>(null);
  const gapsRef = useRef<HTMLDivElement>(null);

  // Fetch analysis when modal opens
  useEffect(() => {
    if (isOpen && jobId && !hasLoaded) {
      fetchAnalysis();
    }
  }, [isOpen, jobId, hasLoaded]);

  // Countdown timer for rate limit errors
  useEffect(() => {
    if (isRateLimitError && retryAfterSeconds !== null && countdown !== null && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null || prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isRateLimitError, retryAfterSeconds, countdown]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAnalysis(null);
      setError(null);
      setIsRateLimitError(false);
      setRetryAfterSeconds(null);
      setCountdown(null);
      setHasLoaded(false);
      setIsFromCache(false);
      setLoadingMessage('');
      setInsufficientBalance(false);
    }
  }, [isOpen]);

  // Listen for insufficient balance modal close events
  useEffect(() => {
    const handleInsufficientBalanceModalClose = () => {
      // When insufficient balance modal closes, check if we should close this modal too
      if (insufficientBalance) {
        // Small delay to ensure smooth transition
        setTimeout(() => {
          onClose();
        }, 300);
      }
    };

    window.addEventListener('insufficient-balance-modal-closed', handleInsufficientBalanceModalClose);
    return () => {
      window.removeEventListener('insufficient-balance-modal-closed', handleInsufficientBalanceModalClose);
    };
  }, [insufficientBalance, onClose]);

  const fetchAnalysis = async () => {
    if (!jobId) return;

    setLoading(true);
    setError(null);
    setAnalysis(null);
    setIsFromCache(false);

    // Start with analyzing message
    setLoadingMessage('Analyzing your fit for this position...');

    try {
      const startTime = Date.now();
      const result = await fitAnalysis.analyzeJobFit(jobId, resumeId, userSearchId, isScrapedJob);
      const endTime = Date.now();
      const duration = endTime - startTime;

      // If response came back very quickly (< 2 seconds), it's likely from cache
      const isQuickResponse = duration < 2000;

      if (isQuickResponse) {
        setLoadingMessage('Retrieved your saved fit analysis');
        setIsFromCache(true);
        // Small delay to show the cache message
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        setLoadingMessage('Analysis complete');
        setIsFromCache(false);
        // Small delay to show completion
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setAnalysis(result);
      setHasLoaded(true);

      // End loading state
      setLoading(false);
      setLoadingMessage('');

      return; // Exit early
    } catch (err: any) {
      console.error('Failed to fetch fit analysis:', err);
      
      // Handle 402 Payment Required (Insufficient Balance)
      // The API interceptor will show the InsufficientBalanceModal,
      // but we also need to handle the state here so modal doesn't become blank
      if (err.response?.status === 402) {
        setLoading(false);
        setLoadingMessage('');
        setInsufficientBalance(true);
        // Don't set error - let InsufficientBalanceModal handle the main message
        return;
      }
      
      // Handle 429 Rate Limit errors with friendly styling
      if (err.response?.status === 429) {
        const detail = err.response?.data?.detail;
        let errorMessage: string;
        let waitSeconds: number | null = null;
        
        if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
          // Extract the friendly message from backend
          errorMessage = detail.message || 'Please wait a moment before trying again.';
          // Extract retry_after_seconds if available
          waitSeconds = detail.retry_after_seconds || null;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = 'Please wait a moment before trying again. Requests are coming in a bit too quickly.';
        }
        
        setError(errorMessage);
        setIsRateLimitError(true); // Mark as rate limit for friendly styling
        setRetryAfterSeconds(waitSeconds);
        setCountdown(waitSeconds); // Start countdown
        setLoading(false);
        setLoadingMessage('');
        return;
      }
      
      // For other errors, extract message properly
      const detail = err.response?.data?.detail;
      let errorMessage: string;
      
      if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
        // If detail is an object, extract the message field
        errorMessage = detail.message || detail.error || JSON.stringify(detail);
      } else if (typeof detail === 'string') {
        // If detail is already a string, use it
        errorMessage = detail;
      } else {
        // Fallback to error message or default
        errorMessage = err.message || 'Failed to analyze job fit';
      }
      
      setError(errorMessage);
      setIsRateLimitError(false);
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const getFitIcon = (assessment: string) => {
    const lowerAssessment = assessment.toLowerCase();
    if (lowerAssessment.includes('strong match') || lowerAssessment.includes('exceeds')) {
      return { icon: checkmarkCircleOutline, color: '#10b981' }; // Green for positive
    } else if (lowerAssessment.includes('partial') || lowerAssessment.includes('gap')) {
      return { icon: warningOutline, color: '#f59e0b' }; // Yellow/amber for warning
    } else if (lowerAssessment.includes('no clear evidence') || lowerAssessment.includes('missing')) {
      return { icon: closeCircleOutline, color: '#ef4444' }; // Red for negative
    }
    return { icon: checkmarkCircleOutline, color: '#10b981' }; // Default to green
  };

  const getFitType = (assessment: string): 'match' | 'gap' => {
    const lowerAssessment = assessment.toLowerCase();
    if (lowerAssessment.includes('strong match') || lowerAssessment.includes('exceeds')) {
      return 'match';
    }
    return 'gap'; // Partial, missing, or no evidence counts as gap
  };

  const calculateMatchStats = () => {
    if (!analysis?.line_item_evaluation) return { matches: 0, gaps: 0 };

    const matches = analysis.line_item_evaluation.filter(item =>
      getFitType(item.fit_assessment) === 'match'
    ).length;

    const gaps = analysis.line_item_evaluation.length - matches;

    return { matches, gaps };
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Am I a Fit?</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {/* Job Title */}
        {jobTitle && (
          <IonText>
            <h2 style={{ marginTop: 0, fontSize: '1.3rem', fontWeight: 600, color: '#1e3a8a' }}>
              {jobTitle}
            </h2>
          </IonText>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            minHeight: '300px'
          }}>
            {/* Animated Loader */}
            <div style={{
              position: 'relative',
              width: '80px',
              height: '80px',
              marginBottom: '32px'
            }}>
              {/* Outer rotating circle */}
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #1e3a8a',
                borderRadius: '50%',
                animation: 'spin 1.2s linear infinite'
              }} />

              {/* Inner pulsing circle */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '50px',
                height: '50px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                borderRadius: '50%',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />

              {/* Sparkles icon in center */}
              <IonIcon
                icon={sparklesOutline}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '28px',
                  color: 'white',
                  zIndex: 1
                }}
              />
            </div>

            {/* Loading Message */}
            <h3 style={{
              margin: '0 0 12px',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937',
              textAlign: 'center'
            }}>
              {loadingMessage || 'Processing...'}
            </h3>

            {/* Subtext */}
            <p style={{
              margin: 0,
              fontSize: '0.95rem',
              color: '#6b7280',
              textAlign: 'center',
              maxWidth: '300px',
              lineHeight: '1.5'
            }}>
              {isFromCache
                ? 'We found your previously generated fit analysis'
                : 'AI is analyzing how well you match this position'
              }
            </p>

            {/* Progress dots */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginTop: '24px'
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#1e3a8a',
                    animation: `dotPulse 1.4s ease-in-out ${i * 0.2}s infinite`
                  }}
                />
              ))}
            </div>

            {/* Add animations */}
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }

              @keyframes pulse {
                0%, 100% {
                  transform: translate(-50%, -50%) scale(1);
                  opacity: 1;
                }
                50% {
                  transform: translate(-50%, -50%) scale(0.95);
                  opacity: 0.8;
                }
              }

              @keyframes dotPulse {
                0%, 100% {
                  opacity: 0.2;
                  transform: scale(0.8);
                }
                50% {
                  opacity: 1;
                  transform: scale(1.2);
                }
              }
            `}</style>
          </div>
        )}

        {/* Insufficient Balance State */}
        {insufficientBalance && !loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            minHeight: '300px',
            textAlign: 'center'
          }}>
            <IonIcon
              icon={warningOutline}
              style={{
                fontSize: '64px',
                color: '#f59e0b',
                marginBottom: '24px'
              }}
            />
            <h3 style={{
              margin: '0 0 12px',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937'
            }}>
              Insufficient Credits
            </h3>
            <p style={{
              margin: '0 0 24px',
              fontSize: '0.95rem',
              color: '#6b7280',
              maxWidth: '300px',
              lineHeight: '1.5'
            }}>
              You don't have enough credits to perform this analysis. Please add credits to continue.
            </p>
            <IonButton
              expand="block"
              fill="solid"
              onClick={onClose}
              style={{
                maxWidth: '200px',
                '--background': '#1e3a8a',
                '--color': 'white',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Close
            </IonButton>
          </div>
        )}

        {/* Error State */}
        {error && !loading && !insufficientBalance && (
          <IonCard style={{ 
            '--background': isRateLimitError ? '#fffbeb' : '#fff3f3', 
            border: isRateLimitError ? '1px solid #f59e0b' : '1px solid #eb445a' 
          }}>
            <IonCardContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <IonIcon 
                  icon={isRateLimitError ? warningOutline : closeCircleOutline} 
                  style={{ 
                    fontSize: '24px', 
                    color: isRateLimitError ? '#f59e0b' : '#eb445a' 
                  }} 
                />
                <div style={{ flex: 1 }}>
                  <IonText style={{ color: isRateLimitError ? '#92400e' : '#eb445a' }}>
                    <strong>{isRateLimitError ? 'Please Wait' : 'Analysis Failed'}</strong>
                  </IonText>
                  <p style={{ 
                    margin: '4px 0 0', 
                    fontSize: '0.9rem', 
                    color: isRateLimitError ? '#78350f' : '#666',
                    lineHeight: '1.5'
                  }}>
                    {error}
                  </p>
                  {isRateLimitError && countdown !== null && countdown > 0 && (
                    <p style={{ 
                      margin: '8px 0 0', 
                      fontSize: '0.85rem', 
                      color: '#92400e',
                      fontWeight: 600
                    }}>
                      ⏱️ Try again in {countdown} second{countdown !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
              <IonButton 
                expand="block" 
                fill={isRateLimitError ? 'solid' : 'outline'} 
                color={isRateLimitError ? 'warning' : 'danger'} 
                onClick={fetchAnalysis} 
                disabled={isRateLimitError && countdown !== null && countdown > 0}
                style={{ marginTop: '16px' }}
              >
                {isRateLimitError && countdown !== null && countdown > 0 
                  ? `Wait ${countdown}s` 
                  : 'Try Again'}
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        {/* Success State - Show Analysis */}
        {analysis && !loading && !insufficientBalance && (
          <>
            {/* Cache Notification Banner */}
            {isFromCache && (
              <div style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: '1px solid #93c5fd',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 2px 8px rgba(30, 58, 138, 0.1)',
                animation: 'slideDown 0.4s ease-out'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ fontSize: '20px' }}>✨</span>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    margin: '0 0 4px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#1e40af'
                  }}>
                    Previously Generated Fit Analysis
                  </h4>
                  <p style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    color: '#1e3a8a',
                    lineHeight: '1.4'
                  }}>
                    We found your saved fit analysis for this job.
                  </p>
                </div>
              </div>
            )}

            {/* Add slide down animation */}
            <style>{`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>

            {/* Analysis Metadata Badge */}
            {analysis.analysis_metadata && analysis.analysis_metadata.used_master_resume && (
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: '1px solid #86efac',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  flexShrink: 0
                }}>
                  ✨
                </div>
                <div style={{ flex: 1 }}>
                  <IonText>
                    <p style={{
                      margin: 0,
                      fontSize: '0.9rem',
                      color: '#065f46',
                      lineHeight: '1.4',
                      fontWeight: 500
                    }}>
                      <strong>Comprehensive Analysis</strong> - Analyzed across {analysis.analysis_metadata.total_resumes_analyzed} resume{analysis.analysis_metadata.total_resumes_analyzed > 1 ? 's' : ''} for the most accurate match
                    </p>
                  </IonText>
                </div>
              </div>
            )}

            {/* Job Match Analysis Summary Tiles */}
            {(() => {
              const { matches, gaps } = calculateMatchStats();
              return (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    margin: '0 0 16px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#1e3a8a',
                    textAlign: 'center'
                  }}>
                    Job Match Analysis
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    {/* Matches Tile */}
                    <div
                      onClick={() => scrollToSection(matchesRef)}
                      style={{
                        background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                        border: '2px solid #10b981',
                        borderRadius: '12px',
                        padding: '20px 16px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.15)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '8px'
                      }}>
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          style={{ fontSize: '32px', color: '#10b981' }}
                        />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: '2rem',
                          fontWeight: 700,
                          color: '#065f46',
                          marginBottom: '4px'
                        }}>
                          {matches}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#047857'
                        }}>
                          Matches
                        </div>
                      </div>
                    </div>

                    {/* Gaps Tile */}
                    <div
                      onClick={() => scrollToSection(gapsRef)}
                      style={{
                        background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
                        border: '2px solid #f59e0b',
                        borderRadius: '12px',
                        padding: '20px 16px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.15)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '8px'
                      }}>
                        <IonIcon
                          icon={warningOutline}
                          style={{ fontSize: '32px', color: '#f59e0b' }}
                        />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: '2rem',
                          fontWeight: 700,
                          color: '#92400e',
                          marginBottom: '4px'
                        }}>
                          {gaps}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#b45309'
                        }}>
                          Gaps
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Role Level Fit Summary Card */}
            <IonCard style={{ '--background': '#1e3a8a', marginBottom: '20px', border: 'none' }}>
              <IonCardContent>
                <IonText color="light">
                  <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600, color: '#ffffff' }}>
                    Role Level Fit
                  </h3>
                  <p style={{ margin: 0, fontSize: '1.05rem', lineHeight: '1.5', color: '#ffffff' }}>
                    {analysis.role_level_fit.conclusion}
                  </p>
                </IonText>
              </IonCardContent>
            </IonCard>

            {/* Skill Category Breakdown Section */}
            {analysis.skill_category_breakdown && (
              <IonCard style={{ '--background': 'linear-gradient(135deg, #e9d5ff 0%, #f3e8ff 100%)', border: '2px solid #a78bfa', marginBottom: '20px' }}>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: '1.2rem', fontWeight: 600, color: '#6b21a8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IonIcon icon={trendingUpOutline} style={{ fontSize: '24px' }} />
                    Skill Match Breakdown
                  </IonCardTitle>
                  <IonCardSubtitle style={{ fontSize: '0.95rem', marginTop: '4px', color: '#7c3aed' }}>
                    Overall Match: {Math.round(analysis.skill_category_breakdown.overall_weighted_score * 100)}%
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  {analysis.skill_category_breakdown.category_scores.map((category, index) => {
                    const getCategoryIcon = (categoryName: string) => {
                      if (categoryName.includes('Core Technical')) return codeSlashOutline;
                      if (categoryName.includes('Tools')) return constructOutline;
                      if (categoryName.includes('Domain') || categoryName.includes('Industry')) return alertCircleOutline;
                      if (categoryName.includes('Soft Skills')) return peopleOutline;
                      if (categoryName.includes('Methodologies')) return ribbonOutline;
                      return starOutline;
                    };

                    const getAssessmentColor = (assessment: string) => {
                      if (assessment === 'Excellent') return '#10b981';
                      if (assessment === 'Good') return '#3b82f6';
                      if (assessment === 'Fair') return '#f59e0b';
                      return '#ef4444';
                    };

                    return (
                      <div
                        key={index}
                        style={{
                          background: '#ffffff',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd6fe',
                          marginBottom: index === analysis.skill_category_breakdown!.category_scores.length - 1 ? '0' : '12px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IonIcon
                              icon={getCategoryIcon(category.category_name)}
                              style={{ fontSize: '20px', color: '#7c3aed' }}
                            />
                            <IonText>
                              <strong style={{ fontSize: '0.95rem', color: '#6b21a8' }}>
                                {category.category_name}
                              </strong>
                            </IonText>
                          </div>
                          <IonBadge
                            style={{
                              '--background': getAssessmentColor(category.assessment),
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              padding: '4px 10px'
                            }}
                          >
                            {category.assessment}
                          </IonBadge>
                        </div>

                        <div style={{ marginBottom: '8px' }}>
                          <IonProgressBar
                            value={category.match_score}
                            style={{
                              '--background': '#e9d5ff',
                              '--progress-background': getAssessmentColor(category.assessment),
                              height: '8px',
                              borderRadius: '4px'
                            }}
                          />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <IonText color="medium">
                            <p style={{ margin: 0, fontSize: '0.85rem' }}>
                              {category.matched_skills} of {category.total_skills} skills matched
                            </p>
                          </IonText>
                          <IonText>
                            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#6b21a8' }}>
                              {Math.round(category.match_score * 100)}%
                            </p>
                          </IonText>
                        </div>
                      </div>
                    );
                  })}
                </IonCardContent>
              </IonCard>
            )}

            {/* Resume Recommendation Section */}
            {analysis.resume_recommendation && (
              <IonCard style={{ '--background': 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)', border: '2px solid #3b82f6', marginBottom: '20px' }}>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: '1.2rem', fontWeight: 600, color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>🎯</span>
                    Recommended Resume
                  </IonCardTitle>
                  <IonCardSubtitle style={{ fontSize: '0.9rem', marginTop: '4px', color: '#1e3a8a' }}>
                    Best resume for this job based on skill match
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      background: '#ffffff',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #93c5fd',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <IonText>
                          <strong style={{ fontSize: '1rem', color: '#1e40af' }}>
                            {analysis.resume_recommendation.recommended_resume_name || 'Resume'}
                          </strong>
                        </IonText>
                        <div style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: 600
                        }}>
                          {Math.round(analysis.resume_recommendation.confidence_score * 100)}% Match
                        </div>
                      </div>
                      <IonText color="medium">
                        <p style={{ margin: '8px 0 0', fontSize: '0.9rem', lineHeight: '1.5' }}>
                          {analysis.resume_recommendation.why_this_resume}
                        </p>
                      </IonText>
                    </div>

                    {/* Alternative Resumes */}
                    {analysis.resume_recommendation.alternative_resumes && analysis.resume_recommendation.alternative_resumes.length > 0 && (
                      <div>
                        <IonText>
                          <p style={{ margin: '0 0 8px', fontSize: '0.9rem', fontWeight: 600, color: '#1e3a8a' }}>
                            Alternative Options:
                          </p>
                        </IonText>
                        {analysis.resume_recommendation.alternative_resumes.map((alt, index) => (
                          <div
                            key={index}
                            style={{
                              background: '#f8fafc',
                              padding: '12px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              marginBottom: '8px'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <IonText>
                                <span style={{ fontSize: '0.9rem', color: '#475569' }}>
                                  {alt.resume_name || `Resume ${index + 1}`}
                                </span>
                              </IonText>
                              <span style={{
                                background: '#e2e8f0',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: 600
                              }}>
                                {Math.round(alt.score * 100)}%
                              </span>
                            </div>
                            {alt.note && (
                              <IonText color="medium">
                                <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>
                                  {alt.note}
                                </p>
                              </IonText>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </IonCardContent>
              </IonCard>
            )}

            {/* Tailoring Suggestions Section */}
            {analysis.tailoring_suggestions && analysis.tailoring_suggestions.suggestions.length > 0 && (
              <IonCard style={{ '--background': 'linear-gradient(135deg, #fef3c7 0%, #fef9e7 100%)', border: '2px solid #f59e0b', marginBottom: '20px' }}>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: '1.2rem', fontWeight: 600, color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>💡</span>
                    Improve Your Match
                  </IonCardTitle>
                  <IonCardSubtitle style={{ fontSize: '0.9rem', marginTop: '4px', color: '#78350f' }}>
                    {analysis.tailoring_suggestions.improvement_potential}
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList style={{ background: 'transparent' }}>
                    {analysis.tailoring_suggestions.suggestions.map((suggestion, index) => (
                      <IonItem key={index} lines={index === analysis.tailoring_suggestions!.suggestions.length - 1 ? 'none' : 'full'} style={{ '--background': 'transparent' }}>
                        <div style={{ width: '100%', padding: '12px 0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <div style={{
                              background: '#f59e0b',
                              color: 'white',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                            <IonText>
                              <strong style={{ fontSize: '1rem', color: '#92400e' }}>
                                {suggestion.skill_name}
                              </strong>
                            </IonText>
                            {suggestion.is_core_technical && (
                              <IonBadge
                                style={{
                                  '--background': 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  padding: '4px 8px',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                HIGH PRIORITY
                              </IonBadge>
                            )}
                            {suggestion.found_in_resume_name && (
                              <span style={{ fontSize: '0.85rem', color: '#78350f' }}>
                                from {suggestion.found_in_resume_name}
                              </span>
                            )}
                          </div>
                          <div style={{ marginLeft: '32px', marginBottom: '8px' }}>
                            <IonText>
                              <p style={{ margin: '0 0 4px', fontSize: '0.85rem', color: '#a16207', fontStyle: 'italic' }}>
                                {suggestion.skill_category}
                              </p>
                            </IonText>
                            <IonText color="medium">
                              <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5', color: '#78350f' }}>
                                <strong>Why it matters:</strong> {suggestion.reason}
                              </p>
                            </IonText>
                          </div>
                          {suggestion.how_to_add && (
                            <div style={{ marginLeft: '32px', background: '#fffbeb', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                              <IonText>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e', lineHeight: '1.4' }}>
                                  <strong>💡 Tip:</strong> {suggestion.how_to_add}
                                </p>
                              </IonText>
                            </div>
                          )}
                        </div>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {/* Line Item Evaluations - Separated into Matches and Gaps */}
            {(() => {
              const matches = analysis.line_item_evaluation.filter(item =>
                getFitType(item.fit_assessment) === 'match'
              );
              const gaps = analysis.line_item_evaluation.filter(item =>
                getFitType(item.fit_assessment) === 'gap'
              );

              return (
                <>
                  {/* Matches Section */}
                  {matches.length > 0 && (
                    <div ref={matchesRef}>
                      <IonCard style={{ '--background': '#ffffff', border: '1px solid #e9ecef', marginBottom: '20px' }}>
                        <IonCardHeader>
                          <IonCardTitle style={{ fontSize: '1.2rem', fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: '24px' }} />
                            Matches ({matches.length})
                          </IonCardTitle>
                          <IonCardSubtitle style={{ fontSize: '0.9rem', marginTop: '4px' }}>
                            Requirements where you have strong evidence
                          </IonCardSubtitle>
                        </IonCardHeader>
                        <IonCardContent style={{ padding: '0' }}>
                          <IonList>
                            {matches.map((item, index) => {
                              const fitIconData = getFitIcon(item.fit_assessment);
                              return (
                                <IonItem key={index} lines={index === matches.length - 1 ? 'none' : 'full'}>
                                  <div style={{ width: '100%', padding: '12px 0' }}>
                                    {/* Requirement */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                                      <IonIcon
                                        icon={fitIconData.icon}
                                        style={{ fontSize: '20px', color: fitIconData.color, marginTop: '2px', flexShrink: 0 }}
                                      />
                                      <div style={{ flex: 1 }}>
                                        <IonLabel>
                                          <strong style={{ fontSize: '0.95rem', color: '#212529' }}>
                                            {item.job_requirement}
                                          </strong>
                                        </IonLabel>
                                      </div>
                                    </div>

                                    {/* Evidence */}
                                    <div style={{ marginLeft: '28px', marginBottom: '8px' }}>
                                      <IonText color="medium">
                                        <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>
                                          <strong>Your Experience:</strong> {item.resume_evidence}
                                        </p>
                                      </IonText>
                                    </div>

                                    {/* Assessment */}
                                    <div style={{ marginLeft: '28px' }}>
                                      <IonText style={{ color: fitIconData.color }}>
                                        <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 500 }}>
                                          {item.fit_assessment}
                                        </p>
                                      </IonText>
                                    </div>
                                  </div>
                                </IonItem>
                              );
                            })}
                          </IonList>
                        </IonCardContent>
                      </IonCard>
                    </div>
                  )}

                  {/* Gaps Section */}
                  {gaps.length > 0 && (
                    <div ref={gapsRef}>
                      <IonCard style={{ '--background': '#ffffff', border: '1px solid #e9ecef' }}>
                        <IonCardHeader>
                          <IonCardTitle style={{ fontSize: '1.2rem', fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IonIcon icon={warningOutline} style={{ fontSize: '24px' }} />
                            Gaps ({gaps.length})
                          </IonCardTitle>
                          <IonCardSubtitle style={{ fontSize: '0.9rem', marginTop: '4px' }}>
                            Areas where you may need to highlight relevant experience
                          </IonCardSubtitle>
                        </IonCardHeader>
                        <IonCardContent style={{ padding: '0' }}>
                          <IonList>
                            {gaps.map((item, index) => {
                              const fitIconData = getFitIcon(item.fit_assessment);
                              return (
                                <IonItem key={index} lines={index === gaps.length - 1 ? 'none' : 'full'}>
                                  <div style={{ width: '100%', padding: '12px 0' }}>
                                    {/* Requirement */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                                      <IonIcon
                                        icon={fitIconData.icon}
                                        style={{ fontSize: '20px', color: fitIconData.color, marginTop: '2px', flexShrink: 0 }}
                                      />
                                      <div style={{ flex: 1 }}>
                                        <IonLabel>
                                          <strong style={{ fontSize: '0.95rem', color: '#212529' }}>
                                            {item.job_requirement}
                                          </strong>
                                        </IonLabel>
                                      </div>
                                    </div>

                                    {/* Evidence */}
                                    <div style={{ marginLeft: '28px', marginBottom: '8px' }}>
                                      <IonText color="medium">
                                        <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>
                                          <strong>Your Experience:</strong> {item.resume_evidence}
                                        </p>
                                      </IonText>
                                    </div>

                                    {/* Assessment */}
                                    <div style={{ marginLeft: '28px' }}>
                                      <IonText style={{ color: fitIconData.color }}>
                                        <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 500 }}>
                                          {item.fit_assessment}
                                        </p>
                                      </IonText>
                                    </div>
                                  </div>
                                </IonItem>
                              );
                            })}
                          </IonList>
                        </IonCardContent>
                      </IonCard>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Action Button */}
            <IonButton
              expand="block"
              onClick={onClose}
              style={{
                marginTop: '20px',
                '--background': '#1e3a8a',
                '--background-hover': '#1e40af',
                '--color': 'white',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Close
            </IonButton>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default FitAnalysisModal;
